/**
 * Overview Tab Component for Client Detail Page
 */
import { Link } from "react-router-dom";
import { livingSituationLabel } from "@/lib/livingSituation";
import axios from "axios";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer, Edit, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AssessmentCycleInfo from "@/components/assessments/AssessmentCycleInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VerificationBadge({ verifiedAt, verifiedByName, onVerify, verifying }) {
  if (!verifiedAt) {
    return (
      <div className="flex items-center gap-2 mt-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <AlertTriangle className="w-3 h-3" /> Not verified
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5 text-xs gap-1.5"
          onClick={onVerify}
          disabled={verifying}
          data-testid="insurance-verify-btn"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {verifying ? "Marking..." : "Mark Verified"}
        </Button>
      </div>
    );
  }
  const verified = new Date(verifiedAt);
  const days = Math.floor((Date.now() - verified.getTime()) / (1000 * 60 * 60 * 24));
  const dateStr = verified.toLocaleDateString();
  let chip;
  if (days <= 60) {
    chip = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        <ShieldCheck className="w-3 h-3" /> Verified {dateStr}
      </span>
    );
  } else if (days <= 90) {
    chip = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <AlertTriangle className="w-3 h-3" /> Re-verify ({days}d ago)
      </span>
    );
  } else {
    chip = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <AlertTriangle className="w-3 h-3" /> Expired ({days}d ago)
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap mt-2">
      {chip}
      {verifiedByName && (
        <span className="text-xs text-slate-500">by {verifiedByName}</span>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2.5 text-xs gap-1.5"
        onClick={onVerify}
        disabled={verifying}
        data-testid="insurance-reverify-btn"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        {verifying ? "Marking..." : "Re-verify"}
      </Button>
    </div>
  );
}

export function OverviewTab({ client, onPrintFaceSheet, onClientUpdate }) {
  const [verifying, setVerifying] = useState(false);

  const handleVerifyInsurance = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/clients/${client.id}/verify-insurance`);
      toast.success("Insurance marked as verified");
      onClientUpdate &&
        onClientUpdate({
          ...client,
          insurance_verified_at: res.data.insurance_verified_at,
          insurance_verified_by_name: res.data.insurance_verified_by_name,
        });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark verified");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div data-testid="client-overview-tab">
      <div className="flex justify-end gap-2 mb-4">
        <Link to={`/clients/${client.id}/edit`}>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="edit-overview-btn"
          >
            <Edit className="w-4 h-4" />
            Edit Personal Info
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={onPrintFaceSheet}
          className="gap-2"
          data-testid="print-face-sheet-btn"
        >
          <Printer className="w-4 h-4" />
          Print Face Sheet
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="form-section">
          <h3 className="form-section-title">Personal Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <p className="info-label">Full Name</p>
              <p className="info-value">
                {client.first_name} {client.last_name}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Date of Birth</p>
              <p className="info-value">{client.dob || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Gender</p>
              <p className="info-value capitalize" data-testid="client-gender-value">{client.gender ? client.gender.replace("unknown", "Unknown / not stated") : "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Social Security Number</p>
              <p className="info-value" data-testid="client-ssn-masked">
                {client.ssn_last4 ? `•••-••-${client.ssn_last4}` : "Not provided"}
                {client.ssn_on_file && <span className="ml-2 text-[10px] rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Full SSN on file</span>}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Phone</p>
              <p className="info-value">{client.phone || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Email</p>
              <p className="info-value" data-testid="overview-email">
                {client.email || "Not provided"}
              </p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h3 className="form-section-title">Address</h3>
          <div className="info-grid">
            <div className="info-item col-span-full">
              <p className="info-label">Street Address</p>
              <p className="info-value">{client.address || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">City</p>
              <p className="info-value">{client.city || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">State</p>
              <p className="info-value">{client.state || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">ZIP Code</p>
              <p className="info-value">{client.zip_code || "Not provided"}</p>
            </div>
            <div className="info-item md:col-span-2">
              <p className="info-label">Living Situation</p>
              <p className="info-value" data-testid="client-living-situation-value">{livingSituationLabel(client.living_situation) || "Not provided"}</p>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="form-section">
          <h3 className="form-section-title">Emergency Contact</h3>
          <div className="info-grid">
            <div className="info-item">
              <p className="info-label">Name</p>
              <p className="info-value">
                {client.emergency_contact_name || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Phone</p>
              <p className="info-value">
                {client.emergency_contact_phone || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Relationship</p>
              <p className="info-value">
                {client.emergency_contact_relation || "Not provided"}
              </p>
            </div>
          </div>
        </div>

        {/* Physician */}
        <div className="form-section">
          <h3 className="form-section-title">Physician</h3>
          <div className="info-grid">
            <div className="info-item">
              <p className="info-label">Physician Name</p>
              <p className="info-value" data-testid="overview-physician-name">
                {client.physician?.name || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Clinic</p>
              <p className="info-value" data-testid="overview-physician-clinic">
                {client.physician?.clinic_name || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Phone</p>
              <p className="info-value" data-testid="overview-physician-phone">
                {client.physician?.phone || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Fax</p>
              <p className="info-value" data-testid="overview-physician-fax">
                {client.physician?.fax || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Address</p>
              <p className="info-value">
                {client.physician?.address || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Last MD Visit</p>
              <p className="info-value">
                {client.last_md_visit || "Not provided"}
              </p>
            </div>
          </div>
          <div className="mt-3">
            <Link to={`/clients/${client.id}/edit#physician`}>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="edit-physician-btn"
              >
                <Edit className="w-4 h-4" />
                Edit Physician
              </Button>
            </Link>
          </div>
        </div>

        {/* Insurance */}
        <div className="form-section">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
            <h3 className="form-section-title m-0">Insurance</h3>
            <VerificationBadge
              verifiedAt={client.insurance_verified_at}
              verifiedByName={client.insurance_verified_by_name}
              onVerify={handleVerifyInsurance}
              verifying={verifying}
            />
          </div>
          <div className="info-grid">
            <div className="info-item">
              <p className="info-label">Insurance Company</p>
              <p className="info-value" data-testid="overview-insurance-company">
                {client.insurance_company || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Policy / Member #</p>
              <p className="info-value" data-testid="overview-insurance-policy">
                {client.insurance_policy_number || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Medicaid ID</p>
              <p className="info-value" data-testid="overview-medicaid-id">
                {client.medicaid_id || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Medicare ID (MBI)</p>
              <p className="info-value" data-testid="overview-medicare-id">
                {client.medicare_id || "Not provided"}
              </p>
            </div>
            <div className="info-item">
              <p className="info-label">Additional Notes</p>
              <p className="info-value">{client.insurance_info || "Not provided"}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Accepted</p>
              <p className="info-value">
                {client.insurance_accepted ? "Yes" : "No"}
              </p>
            </div>
          </div>
          <AssessmentCycleInfo clientId={client.id} insuranceCompany={client.insurance_company} />
        </div>
      </div>
    </div>
  );
}
